import React, { useRef, useState } from "react";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import "../../../../../sass/components/my-documents-page.scss";

import { DocumentsTabs } from "./DocumentsTabs";
import { DPATipsWhatIs } from "./DPATips";
import { useDpas, useSuggestedDpas } from "../../../hooks/useDataProtectionAssessment";
import { usePrimaryOrganizationId } from "../../../hooks";
import { RouteHelpers, Routes } from "../../../root/routes";
import { useHistory } from "react-router";
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useActionRequest } from "../../../../common/hooks/api";
import {
  CreateDataProtectionAssessmentRequest,
  DPAType,
  dpaTypeToHumanReadable,
} from "../../../../common/service/server/dto/DataProtectionAssessmentDto";
import { Api } from "../../../../common/service/Api";
import { ConfirmDialog } from "../../../components/dialog";
import { Breadcrumbs } from "@truevault/aquilo";

const CreateDPAButton = ({ createDpa }: { createDpa: (type: DPAType) => void }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleMenuItemClick = (type: DPAType) => {
    setOpen(false);
    createDpa(type);
  };

  return (
    <>
      <ButtonGroup variant="contained" ref={anchorRef}>
        <Button onClick={() => setOpen((o) => !o)} endIcon={<Add />}>
          Create New Assessment
        </Button>
        <Popper
          sx={{ zIndex: 1 }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{ transformOrigin: placement === "bottom" ? "center top" : "center bottom" }}
            >
              <Paper>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                  <MenuList autoFocusItem id="menu-list-grow">
                    <MenuItem onClick={() => handleMenuItemClick("TARGETED_ADVERTISING")}>
                      Targeted Advertising
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick("DATA_SELLING_GOOGLE_ANALYTICS")}>
                      Data Selling - Google Analytics
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick("DATA_SELLING_OTHER")}>
                      Data Selling - Other
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick("SENSITIVE_DATA")}>
                      Sensitive Data
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick("PERSONAL_DATA_OF_CHILDREN")}>
                      Personal Data of Children
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick("PROFILING")}>Profiling</MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick("SNI")}>
                      Shopify Network Intelligence
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </ButtonGroup>
    </>
  );
};

export const iconForDpaType = (type: DPAType) => {
  switch (type) {
    case "TARGETED_ADVERTISING":
      return "dpa_targeted_advertising.svg";
    case "DATA_SELLING":
      return "dpa_data_selling.svg";
    case "DATA_SELLING_GOOGLE_ANALYTICS":
      return "dpa_data_selling.svg";
    case "DATA_SELLING_OTHER":
      return "dpa_data_selling.svg";
    case "SENSITIVE_DATA":
      return "dpa_sensitive_data.svg";
    case "PERSONAL_DATA_OF_CHILDREN":
      return "dpa_children.svg";
    case "PROFILING":
      return "dpa_profiling.svg";
    case "SNI":
      return "dpa_shopify.svg";
  }
};

const DPAInfoBox = ({
  dpaType,
  buttonText,
  onClick,
  showDelete,
  deleteFn,
}: {
  dpaType: DPAType;
  buttonText: string;
  onClick: () => void;
  showDelete?: boolean;
  deleteFn?: (abandonedNote: string) => void;
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [abandonedNote, setAbandonedNote] = useState("");

  return (
    <div className="dpa-info-box">
      <div className="dpa-info-box__wrapper">
        <div className="dpa-info-box__dpa-type">
          <img src={`/assets/images/stay_compliant/dpa/${iconForDpaType(dpaType)}`} alt="DPA" />
          <div className="text-weight-bold text-t4 ml-xs">{dpaTypeToHumanReadable(dpaType)}</div>
        </div>
        <div
          className={showDelete === true ? `d-flex justify-space-between align-items-center` : ``}
        >
          <Button variant="contained" color="primary" onClick={onClick}>
            {buttonText}
          </Button>
          {showDelete === true && (
            <a
              className="ml-sm"
              style={{ color: "#0288D1" }}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsDeleteDialogOpen(true);
              }}
            >
              Delete
            </a>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        confirmText="Delete Assessment"
        confirmTitle={`This will delete "${dpaTypeToHumanReadable(dpaType)}"`}
        contentText={
          <>
            <p>You won't be able to view or download this assessment once it's deleted.</p>
            <div>
              <span className="color-neutral-700 text-t1">Add a deletion note (optional)</span>
              <TextField
                className="w-100 mb-sm"
                fullWidth={false}
                multiline={true}
                rows={6}
                variant="outlined"
                value={abandonedNote}
                onChange={(e) => setAbandonedNote(e.target.value)}
              />
            </div>
          </>
        }
        onConfirm={async () => {
          await deleteFn(abandonedNote);
          setIsDeleteDialogOpen(false);
        }}
      />
    </div>
  );
};

const isWithinLastNumberOfYears = (date: string, years: number) => {
  const oldestYear = new Date();
  oldestYear.setFullYear(oldestYear.getFullYear() - years);
  return new Date(date) > oldestYear;
};

export const DPAPageCurrent = () => {
  const history = useHistory();

  const orgId = usePrimaryOrganizationId();
  const [dpas, fetchDpaRequestState, refreshDpas] = useDpas(orgId);
  const [suggestedDpas, ,] = useSuggestedDpas(orgId);

  const { fetch: createDpaApiAction } = useActionRequest({
    api: async (createRequestBody: CreateDataProtectionAssessmentRequest) => {
      return await Api.dataProtectionAssessment.createDpa(orgId, createRequestBody);
    },
    onSuccess(result) {
      history.push(RouteHelpers.documents.dataProtectionAssessment.write(result.id));
    },
  });

  const currentDpas = (dpas || []).filter((dpa) => dpa.status == "WRITING");

  const suggstedDpaTypesToShow = Object.keys(suggestedDpas || {}).filter((dpaType: DPAType) => {
    return (
      suggestedDpas &&
      suggestedDpas[dpaType] === true &&
      !(dpas || []).some(
        (dpa) =>
          dpa.type == dpaType &&
          dpa.status !== "ABANDONED" &&
          isWithinLastNumberOfYears(dpa.createdAt, 3),
      )
    );
  });

  return (
    <StandardPageWrapper requests={[fetchDpaRequestState]}>
      <div className="tw-boundary">
        <Breadcrumbs
          title="Data Protection Assessments"
          source="Privacy Docs"
          sourceRoute={Routes.documents.root}
        />
      </div>
      <p className="sub-header">Your Assessments</p>
      <div className="d-flex justify-space-between align-items-baseline">
        <div className="tab-wrapper">
          <DocumentsTabs current="current" />
        </div>
        <CreateDPAButton
          createDpa={(dpaType: DPAType) =>
            createDpaApiAction({
              name: `Data Protection Assessment: ${dpaTypeToHumanReadable(dpaType)}`,
              dpaType,
            } as CreateDataProtectionAssessmentRequest)
          }
        />
      </div>
      {currentDpas.length == 0 && suggstedDpaTypesToShow.length == 0 ? (
        <div className="empty-page">
          <img src="/assets/images/stay_compliant/empty-page.svg" alt="add" />
          <p>No Current Assessments</p>
        </div>
      ) : (
        <div className="dpa-info-box-grid">
          {currentDpas.map((dpa) => (
            <DPAInfoBox
              key={dpa.id}
              buttonText="Continue Assessment"
              dpaType={dpa.type}
              onClick={() =>
                history.push(RouteHelpers.documents.dataProtectionAssessment.write(dpa.id))
              }
              showDelete={true}
              deleteFn={async (abandonedNote: string) => {
                await Api.dataProtectionAssessment.abandonDpa(orgId, dpa.id, { abandonedNote });
                await refreshDpas();
              }}
            />
          ))}
          {suggstedDpaTypesToShow.map((dpaType: DPAType) => (
            <DPAInfoBox
              key={`suggested-${dpaType}`}
              dpaType={dpaType}
              buttonText="Start Assessment"
              onClick={() => {
                createDpaApiAction({
                  name: `Data Protection Assessment: ${dpaTypeToHumanReadable(dpaType)}`,
                  dpaType,
                  copyFromPreviousDpa: true,
                } as CreateDataProtectionAssessmentRequest);
              }}
            />
          ))}
        </div>
      )}
      <p className="sub-header mt-xl">Resources & Tips</p>
      <DPATipsWhatIs />
    </StandardPageWrapper>
  );
};
